import { useState, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiGetShows, type ShowDTO } from '../api';
import { useProfileContext } from '../hooks/useProfileContext';
import PageHeader from '../components/PageHeader';
import { withPageIcon } from '../lib/uiIcons';
import PageLoadError from '../components/PageLoadError';

function hexToRgba(hex: string, alpha: number): string {
  // P3e: the colour may be a CSS token (`var(--accent)` — the Roadcase fallback
  // for a band with no colour). A token parsed as hex tints BLACK, so mix it
  // instead; a real band colour (data) keeps the numeric path below.
  if (!/^#?[\da-f]{3,8}$/i.test(String(hex || '').trim())) {
    return `color-mix(in srgb, ${hex || 'var(--accent)'} ${Math.round(alpha * 100)}%, transparent)`;
  }
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16) || 0;
  const g = parseInt(full.slice(2, 4), 16) || 0;
  const b = parseInt(full.slice(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function monthLabel(key: string) {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

/**
 * Reusable MONTH-GRID calendar view (the component historically mounted inside
 * ShowsPage). SPEC-PERSONAL-CALENDAR (2026-10-21): the page is now ALSO the
 * rendering core of the PERSONAL calendar — the props below let
 * PersonalCalendarPage inject its own filtered show list, banner and header
 * actions while the default (no props) keeps the historical group behaviour
 * byte-for-byte.
 */
export interface CalendarPageProps {
  /** Injected show list — skips the group fetch AND the band filter when set. */
  shows?: ShowDTO[];
  /** Loading state for the injected list. */
  loading?: boolean;
  /** Extra buttons next to the month navigation (e.g. « Add to my calendar »). */
  headerActions?: ReactNode;
  /** Rendered above the grid (e.g. the subscription banner). */
  banner?: ReactNode;
  /** Page title (defaults to the group calendar title). */
  title?: string;
}

export default function CalendarPage(props: CalendarPageProps = {}) {
  const injected = Array.isArray(props.shows);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profiles, activeProfileId } = useProfileContext();
  const bands = useMemo(
    () => Object.fromEntries(profiles.map((p) => [p.id, { name: p.name, color: p.color || 'var(--accent)' }])),
    [profiles],
  );
  const [shows, setShows] = useState<ShowDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // Raw API message, shown as the small context line of the shared state (the
  // guard is a READING surface: it never invents a cause).
  const [loadErrorDetail, setLoadErrorDetail] = useState<string | null>(null);
  const [monthOffset, setMonthOffset] = useState(0);
  // empty array = all bands; otherwise only the checked band ids are shown
  const [filterBands, setFilterBands] = useState<string[]>([]);
  const [userTouched, setUserTouched] = useState(false);

  // Default the filter to the active band once it is known (unless the user
  // has already interacted with the checkboxes).
  useEffect(() => {
    if (activeProfileId && !userTouched) setFilterBands([activeProfileId]);
  }, [activeProfileId, userTouched]);

  const load = () => {
    setLoading(true);
    setLoadError(false);
    setLoadErrorDetail(null);
    apiGetShows()
      .then(setShows)
      .catch((e: unknown) => {
        console.error(e);
        setLoadError(true);
        setLoadErrorDetail(e instanceof Error ? e.message : String(e));
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const bandList = useMemo(() => Object.values(bands), [bands]);
  const allChecked = filterBands.length === 0;

  const toggleAll = () => {
    setUserTouched(true);
    setFilterBands([]);
  };
  const toggleBand = (id: string) => {
    setUserTouched(true);
    if (allChecked) {
      // start from "only this one"
      setFilterBands([id]);
    } else if (filterBands.includes(id)) {
      const next = filterBands.filter((x) => x !== id);
      setFilterBands(next); // if empty -> back to "all"
    } else {
      setFilterBands([...filterBands, id]);
    }
  };

  const now = new Date();
  const target = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const targetKey = `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, '0')}`;

  // Injected list (personal calendar): the parent owns loading/errors AND the
  // filter — no band checkboxes, no group fetch on this path.
  const effShows = injected ? (props.shows as ShowDTO[]) : shows;
  const effLoading = injected ? !!props.loading : loading;

  const grouped = useMemo(() => {
    const map: Record<string, Record<number, ShowDTO[]>> = {};
    for (const s of effShows) {
      if (!s.date) continue;
      if (!injected && filterBands.length > 0 && !filterBands.includes(s.band_id || '')) continue;
      const dt = new Date(s.date);
      const k = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      if (k !== targetKey) continue;
      const day = dt.getDate();
      if (!map[k]) map[k] = {};
      if (!map[k][day]) map[k][day] = [];
      map[k][day].push(s);
    }
    return map[targetKey] || {};
  }, [effShows, targetKey, filterBands, injected]);

  const daysInMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  const firstWeekday = new Date(target.getFullYear(), target.getMonth(), 1).getDay();

  // Lot 3a: the calendar NEEDS its shows — without them the grid is a month of
  // empty cells that reads as "nothing programmed" instead of "the request
  // failed". The failure replaces the grid with the shared, readable state.
  if (loadError && !injected) {
    return <PageLoadError title={props.title || t('calendar.title')} detail={loadErrorDetail} onRetry={load} />;
  }

  return (
    <div className="page" data-density="socle">
      <PageHeader
        title={props.title || withPageIcon('calendar.title', t('calendar.title'))}
        actions={
          <>
            {props.headerActions}
            <button className="btn btn-secondary" onClick={() => setMonthOffset((o) => o - 1)}>{t('calendar.prev')}</button>
            <span style={{ alignSelf: 'center', fontWeight: 600 }}>{monthLabel(targetKey)}</span>
            <button className="btn btn-secondary" onClick={() => setMonthOffset((o) => o + 1)}>{t('calendar.next')}</button>
          </>
        }
      />

      {props.banner}

      {!injected && bandList.length > 1 && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', margin: '8px 0 16px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 'var(--font-md)' }}>
          <input type="checkbox" checked={allChecked} onChange={toggleAll} />
          {t('calendar.all')}
        </label>
        {bandList.map((b) => {
          const id = Object.keys(bands).find((k) => bands[k].name === b.name) || '';
          return (
            <label key={b.name} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 'var(--font-md)' }}>
              <input
                type="checkbox"
                checked={allChecked || filterBands.includes(id)}
                onChange={() => id && toggleBand(id)}
              />
              <span style={{
                width: 12, height: 12, borderRadius: 3, display: 'inline-block',
                background: b.color, border: '1px solid var(--border)',
              }} />
              {b.name}
            </label>
          );
        })}
      </div>
      )}

      {effLoading ? <div className="loading">{t('calendar.loading')}</div> : (
        <div className="calendar-grid" style={{
          display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, marginTop: 16,
        }}>
          {['calendar.sun', 'calendar.mon', 'calendar.tue', 'calendar.wed', 'calendar.thu', 'calendar.fri', 'calendar.sat'].map((dk) => (
            <div key={dk} style={{ textAlign: 'center', fontWeight: 600, fontSize: 'var(--font-xs)', color: 'var(--text-dim)', padding: 6 }}>
              {t(dk)}
            </div>
          ))}
          {Array.from({ length: firstWeekday }).map((_, i) => (
            <div key={`empty-${i}`} />
          ))}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dayShows = grouped[day] || [];
            return (
              <div key={day} className="calendar-cell" style={{
                height: 150, border: '1px solid var(--border)', borderRadius: 6, padding: 6,
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
                background: dayShows.length ? 'var(--accent-tint)' : 'var(--surface)',
              }}>
                <div style={{ fontSize: 'var(--font-xs)', fontWeight: 700, color: 'var(--text-dim)', flex: '0 0 auto' }}>{day}</div>
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', marginTop: 2 }}>
                {dayShows.map((s) => {
                  const band = bands[s.band_id || ''];
                  const evColor = band?.color || 'var(--accent)';
                  const isPast = s.date ? new Date(s.date).getTime() < Date.now() : false;
                  const city = [s.city, s.country].filter(Boolean).join(', ');
                  const label = [band?.name, s.name || '', s.venue_name || city].filter(Boolean).join(' · ');
                  return (
                  <div key={s.id}
                    onClick={() => navigate(`/tour/shows/${s.slug || s.id}`)}
                    style={{
                      marginTop: 2, fontSize: '0.72rem', lineHeight: 1.25, padding: '1px 4px', borderRadius: 4,
                      background: hexToRgba(evColor, isPast ? 0.04 : 0.16),
                      borderLeft: `3px solid ${evColor}`,
                      color: 'var(--text)', cursor: 'pointer',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}
                    title={t('calendar.eventTitle', { band: band?.name || t('calendar.unknown'), name: s.name || '', venue: s.venue_name || city || '', type: s.event_type || '' })}
                  >
                    {label}
                  </div>
                  );
                })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}